import * as React from 'react'
import { Box, Typography, Button, CircularProgress, IconButton, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DownloadIcon from '@mui/icons-material/Download'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getPresentation, deletePresentation, duplicatePresentation } from '../../slices/presentation'
import presentationService from '../../services/presentation'

function PresentationSettings() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const presentations = useSelector((state) => state.presentation.presentations)
  const [regenerating, setRegenerating] = React.useState(null)

  React.useEffect(() => {
    dispatch(getPresentation())
  }, [dispatch])

  const handleRegenerate = async (id) => {
    setRegenerating(id)
    try {
      const res = await presentationService.regeneratePresentation(id)
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `presentation_${id}.pptx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Regeneration failed:', err)
    } finally {
      setRegenerating(null)
    }
  }

  const iconBtn = (label, icon, color, onClick, disabled = false) => (
    <Tooltip title={label} arrow>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onClick}
          sx={{
            color,
            border: `1px solid ${color}40`,
            borderRadius: '8px',
            p: 0.7,
            '&:hover': { backgroundColor: `${color}12`, borderColor: color },
            '&.Mui-disabled': { opacity: 0.35 },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  )

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.5rem' }}>
          Presentations
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/settings/presentation/new')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              borderRadius: '8px',
              px: 2.5,
              py: 0.8,
              backgroundColor: '#3b82f6',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#2563eb', boxShadow: 'none' },
            }}
          >
            + New Presentation
          </Button>
        </Box>
      </Box>

      {/* Empty state */}
      {presentations.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10, color: '#94a3b8' }}>
          <Typography variant="h6" sx={{ mb: 1, color: '#64748b', fontWeight: 600 }}>No presentations yet</Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>Click "+ New Presentation" to create your first one.</Typography>
        </Box>
      )}

      {/* Table */}
      {presentations.length > 0 && (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
          {/* Header row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 160px 100px 160px',
              px: 2.5,
              py: 1.2,
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            {['Name', 'Type', 'Slides', 'Actions'].map((h) => (
              <Typography key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {h}
              </Typography>
            ))}
          </Box>

          {/* Data rows */}
          {presentations.map((p, idx) => (
            <Box
              key={p.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 160px 100px 160px',
                px: 2.5,
                py: 1.5,
                alignItems: 'center',
                borderBottom: idx < presentations.length - 1 ? '1px solid #f1f5f9' : 'none',
                '&:hover': { backgroundColor: '#f8fafc' },
                transition: 'background-color 0.15s',
              }}
            >
              {/* Name */}
              <Typography
                onClick={() => navigate(`/settings/presentation/${p.id}/edit`)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  pr: 2,
                }}
              >
                {p.title}
              </Typography>

              {/* Type */}
              <Typography sx={{ fontSize: '0.875rem', color: '#64748b' }}>
                {p.template_path ? 'Template' : '—'}
              </Typography>

              {/* Slides */}
              <Typography sx={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: 500 }}>
                {p.slides_count ?? 0}
              </Typography>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {iconBtn('Edit', <EditIcon fontSize="small" />, '#3b82f6', () => navigate(`/settings/presentation/${p.id}/edit`))}
                {regenerating === p.id
                  ? <CircularProgress size={18} sx={{ mx: 0.5 }} />
                  : iconBtn('Generate & Download', <DownloadIcon fontSize="small" />, '#10b981', () => handleRegenerate(p.id))
                }
                {iconBtn('Duplicate', <ContentCopyIcon fontSize="small" />, '#6366f1', () => dispatch(duplicatePresentation({ id: p.id })))}
                {iconBtn('Delete', <DeleteIcon fontSize="small" />, '#ef4444', () => { if (window.confirm('Do you really want to remove this presentation?')) dispatch(deletePresentation({ id: p.id })); })}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default PresentationSettings
